import { useEffect, useRef } from 'react';
import type { PlayerId } from '@/engine/types';
import { useGameStore } from '@/state/gameStore';

export type AutoStartOptions = {
  tournamentId: string;
  seed: string;
  playerIds: ReadonlyArray<PlayerId>;
  heroId: PlayerId;
  characterCodenames?: ReadonlyArray<string>;
};

/**
 * Kicks off (or resumes) the requested tournament when entering /play.
 * Re-running with the same `tournamentId` is a no-op.
 *
 * Why the in-flight ref: React 18 StrictMode invokes effects twice
 * synchronously. The first invocation starts the async load but the
 * store's `tournamentId` doesn't flip until the promise resolves; the
 * second invocation would otherwise start a *duplicate* tournament that
 * overwrites mid-hand state. The ref is set before the await and
 * cleared in `finally`, so any second invocation short-circuits.
 */
export function useAutoStartTournament(options: AutoStartOptions): void {
  const activeId = useGameStore((s) => s.tournamentId);
  const inFlightRef = useRef<string | null>(null);

  const { tournamentId, seed, playerIds, heroId, characterCodenames } = options;

  useEffect(() => {
    if (activeId === tournamentId) return;
    if (useGameStore.getState().tournamentId === tournamentId) return;
    if (inFlightRef.current === tournamentId) return;

    inFlightRef.current = tournamentId;
    void (async () => {
      try {
        const resumed = await useGameStore.getState().loadResume(tournamentId);
        if (resumed) return;
        await useGameStore.getState().startTournament({
          tournamentId,
          seed,
          playerIds,
          heroId,
          characterCodenames,
        });
      } finally {
        if (inFlightRef.current === tournamentId) inFlightRef.current = null;
      }
    })();
  }, [activeId, tournamentId, seed, playerIds, heroId, characterCodenames]);
}
